import { FormEventHandler, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { MANAGEMENT_USER_LICENSE_SEARCH, MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { getUsers } from "../clients/UserClient";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { User } from "../types/User";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import { formatUtcToLocalDateTime } from "../utils/DateUtils";

/**
 * 管理者向け - DFアカウント検索画面
 * @returns JSX.Element
 */
const ManagementUserSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getRecentToken } = useUserContext();

    const [searchText, setSearchText] = useState<string>("");
    const [searchAttr, setSearchAttr] = useState<string>("email");
    const [searchOpt, setSearchOpt] = useState<number>(1); // 1:完全一致、2:部分一致
    const [userList, setUserList] = useState<User[]>([]);
    const [doneFlg, setDoneFlg] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    /**
     * 処理中フラグ
     *
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    /**
     * 検索押下時のイベント処理
     * @param event
     */
    const handleSearchUser: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        setIsProcessing(true);
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API] ユーザー情報一括取得
            const enablePartialMatch = searchOpt == 2; // 部分一致の場合はtrue
            var req;
            switch (searchAttr) {
                case "email":
                    req = { email: searchText.trim(), enablePartialMatch: enablePartialMatch };
                    break;
                case "familyName":
                    req = { familyName: searchText.trim(), enablePartialMatch: enablePartialMatch };
                    break;
                case "givenName":
                    req = { givenName: searchText.trim(), enablePartialMatch: enablePartialMatch };
                    break;
                case "organization":
                    req = { organization: searchText.trim(), enablePartialMatch: enablePartialMatch };
                    break;
                default:
                    throw new Error("invalid params.");
            }
            const response: any = await getUsers(idToken, req);
            setUserList(response);
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setIsProcessing(false);
            setDoneFlg(true);
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title="DFアカウント検索 | DF WebService" />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">DFアカウント検索</h1>
                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}
                <form className="mx-auto max-w-4xl" onSubmit={handleSearchUser}>
                    <div className="grid grid-cols-4 items-center justify-center gap-6 pb-4 pt-2 sm:m-2">
                        {/* 検索文字列 */}
                        <div className="col-span-2 py-2">
                            <input
                                type="text"
                                id="searchText"
                                placeholder="検索文字列"
                                className="textbox-common"
                                onChange={(e) => setSearchText(e.target.value)}
                                maxLength={100}
                                required
                            />
                        </div>
                        {/* 検索属性 */}
                        <div className="col-span-1 py-2">
                            <select
                                id="selectAttr"
                                value={searchAttr}
                                onChange={(e) => setSearchAttr(e.target.value)}
                                className="w-full rounded-md border border-gray-300 bg-gray-100 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value={"email"}>メールアドレス</option>
                                <option value={"familyName"}>姓</option>
                                <option value={"givenName"}>名</option>
                                <option value={"organization"}>所属</option>
                            </select>
                        </div>
                        {/* 検索オプション */}
                        <div className="col-span-1 py-2">
                            <select
                                id="selectOpt"
                                value={searchOpt}
                                onChange={(e) => setSearchOpt(parseInt(e.target.value))}
                                className="w-full rounded-md border border-gray-300 bg-gray-100 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value={1}>完全一致</option>
                                <option value={2}>部分一致</option>
                            </select>
                        </div>
                    </div>
                    {/* 検索ボタン */}
                    <div className="flex items-center justify-evenly">
                        <button
                            type="submit"
                            className={`${isProcessing ? "btn-primary-disabled" : "btn-primary"} w-[150px] py-2`}
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center">
                                    <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                    <label className="">Processing...</label>
                                </div>
                            ) : (
                                t("CommonLabel.search")
                            )}
                        </button>
                    </div>
                </form>
                <hr className="my-5 border-b border-t-2" />
                {userList.length === 0 ? (
                    // ユーザーが存在しない場合
                    <div className="flex items-center justify-center py-4 font-bold text-gray-700">
                        {doneFlg ? "ユーザーが見つかりませんでした。" : "条件を指定して検索してください。"}
                    </div>
                ) : (
                    // ユーザーが見つかった場合
                    <div className="mx-auto max-w-6xl overflow-x-auto">
                        <div className="flex items-center justify-center font-bold text-gray-700">
                            {userList.length}件のユーザーが見つかりました。
                        </div>
                        <table className="my-3 min-w-full divide-y divide-gray-200 border">
                            <thead className="bg-gray-400">
                                <tr
                                    className={`${
                                        i18n.language === "ja" ? "text-base" : "text-sm"
                                    } font-semibold uppercase tracking-wide text-gray-100`}
                                >
                                    {/* [ヘッダ] メールアドレス */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.email")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 氏 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.familyName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 名 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.givenName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 所属 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.organization")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 登録日時 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.insertDate")}</span>
                                        </div>
                                    </th>
                                    {/* ライセンス確認 */}
                                    <th scope="col" className="whitespace-nowrap px-6 py-1 text-left"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {userList.map((u) => (
                                    <tr key={u.user_id} className="text-sm font-semibold text-gray-800">
                                        {/* メールアドレス */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.email}</span>
                                            </div>
                                        </td>
                                        {/* 氏 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.family_name}</span>
                                            </div>
                                        </td>
                                        {/* 名 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.given_name}</span>
                                            </div>
                                        </td>
                                        {/* 所属 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{u.organization}</span>
                                            </div>
                                        </td>
                                        {/* 登録日時 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-6 py-3">
                                                <span>{formatUtcToLocalDateTime(u.insert_date)}</span>
                                            </div>
                                        </td>
                                        {/* ライセンス確認 */}
                                        <td className="h-px w-px whitespace-nowrap">
                                            <div className="px-3 py-1.5">
                                                <Link
                                                    className="inline-flex items-center gap-x-1.5 text-sm font-medium text-blue-700 hover:underline"
                                                    to={MANAGEMENT_USER_LICENSE_SEARCH}
                                                    state={{ selectedUser: u }}
                                                >
                                                    ライセンス使用状況を確認
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {/* 戻るボタン */}
                <div className="mx-auto flex max-w-xl items-center justify-evenly py-10 ">
                    <button
                        type="button"
                        onClick={() => navigate(MYPAGE_ROUTE)}
                        className="btn-secondary mx-2 w-[100px]"
                        disabled={isProcessing}
                    >
                        {t("CommonLabel.back")}
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    );
};
export default ManagementUserSearch;
